import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Chip,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  LinearProgress,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Flag as FlagIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Event as EventIcon,
  Close as CloseIcon,
  Cake as CakeIcon,
  Wc as GenderIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  VideoCall as VideoCallIcon,
  Book as BookIcon,
  Star as StarIcon,
  AccessTime as AccessTimeIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Circle as CircleIcon,
  CalendarToday as CalendarIcon,
  Grade as GradeIcon,
  PendingActions as PendingIcon,
} from '@mui/icons-material';
import conversationService from '../services/conversationService';
import { getPhoneCountryInfo } from '../utils/phoneUtils';
import { formatInArgentina } from '../utils/timezone';

// Compact detail item for optimized space
const CompactDetailItem = ({ icon: Icon, label, value, inline = false }) => (
  <Box sx={{ 
    display: inline ? 'inline-flex' : 'flex', 
    alignItems: 'center', 
    mb: inline ? 0 : 1,
    mr: inline ? 2 : 0,
  }}>
    <Icon sx={{ color: 'text.secondary', mr: 1, fontSize: 18 }} />
    {inline ? (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {value}
      </Typography>
    ) : (
      <Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.65rem', lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.825rem', lineHeight: 1.3 }}>
          {value || '-'}
        </Typography>
      </Box>
    )}
  </Box>
);

// Activity item with optimized layout
const ActivityItem = ({ activity }) => {
  const getStatusColor = () => {
    if (activity.completed) return '#4caf50';
    if (activity.progress >= 70) return '#ff9800';
    if (activity.progress >= 40) return '#2196f3';
    return '#f44336';
  };

  const getDaysRemaining = () => {
    const deadline = new Date(activity.deadline.split('/').reverse().join('-'));
    const today = new Date();
    const diff = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const daysRemaining = getDaysRemaining();
  const isOverdue = daysRemaining < 0 && !activity.completed;
  const isUrgent = daysRemaining <= 3 && daysRemaining >= 0 && !activity.completed;

  return (
    <Box sx={{ 
      display: 'flex',
      alignItems: 'flex-start',
      p: 1.5,
      mb: 1,
      bgcolor: activity.completed ? '#f8fdf8' : isOverdue ? '#fff8f8' : 'white',
      border: '1px solid',
      borderColor: activity.completed ? '#e8f5e9' : isOverdue ? '#ffebee' : '#f0f0f0',
      borderRadius: 1,
      transition: 'all 0.2s',
      '&:hover': {
        bgcolor: activity.completed ? '#f1fbf1' : isOverdue ? '#fff0f0' : '#fafafa',
        borderColor: activity.completed ? '#c8e6c9' : isOverdue ? '#ffcdd2' : '#e0e0e0',
      }
    }}>
      {/* Status Indicator */}
      <Box sx={{ 
        minWidth: 32,
        height: 32,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: activity.completed ? '#4caf50' : 'transparent',
        border: activity.completed ? 'none' : `2px solid ${getStatusColor()}`,
        mr: 1.5
      }}>
        {activity.completed ? (
          <CheckCircleIcon sx={{ fontSize: 18, color: 'white' }} />
        ) : (
          <Typography variant="caption" sx={{ fontWeight: 700, color: getStatusColor(), fontSize: '0.7rem' }}>
            {activity.progress}%
          </Typography>
        )}
      </Box>

      {/* Activity Content */}
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="body2" sx={{ 
            fontWeight: 600, 
            fontSize: '0.875rem',
            flexGrow: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textDecoration: activity.completed ? 'line-through' : 'none',
            color: activity.completed ? 'text.secondary' : 'text.primary'
          }}>
            {activity.name}
          </Typography>
          {activity.grade && (
            <Chip 
              icon={<GradeIcon sx={{ fontSize: 14 }} />}
              label={activity.grade}
              size="small"
              sx={{ 
                height: 20,
                fontSize: '0.7rem',
                ml: 1,
                bgcolor: '#e3f2fd',
                color: '#1976d2'
              }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="caption" sx={{ 
            color: 'text.secondary', 
            fontSize: '0.75rem',
            display: 'flex',
            alignItems: 'center'
          }}>
            {activity.type === 'Módulo Asincrónico' && <BookIcon sx={{ fontSize: 14, mr: 0.5 }} />}
            {activity.type === 'Presentismo' && <VideoCallIcon sx={{ fontSize: 14, mr: 0.5 }} />}
            {activity.type === 'Evaluación' && <AssignmentIcon sx={{ fontSize: 14, mr: 0.5 }} />}
            {activity.type}
          </Typography>
          
          {!activity.completed && (
            <Typography variant="caption" sx={{ 
              color: isOverdue ? '#f44336' : isUrgent ? '#ff9800' : 'text.secondary',
              fontSize: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              fontWeight: isUrgent || isOverdue ? 600 : 400
            }}>
              {isOverdue && <WarningIcon sx={{ fontSize: 14, mr: 0.5 }} />}
              {isUrgent && <AccessTimeIcon sx={{ fontSize: 14, mr: 0.5 }} />}
              {isOverdue ? `Overdue ${Math.abs(daysRemaining)} days ago` : 
               isUrgent ? `Due in ${daysRemaining} days` : 
               activity.deadline}
            </Typography>
          )}
        </Box>

        {!activity.completed && activity.progress > 0 && (
          <LinearProgress 
            variant="determinate" 
            value={activity.progress} 
            sx={{ 
              mt: 1,
              height: 4, 
              borderRadius: 2,
              bgcolor: '#f0f0f0',
              '& .MuiLinearProgress-bar': {
                bgcolor: getStatusColor()
              }
            }}
          />
        )}
      </Box>
    </Box>
  );
};

// Program accordion with enhanced metrics
const ProgramAccordion = ({ program, expanded, onChange }) => {
  const pendingActivities = program.activities.filter(a => !a.completed && a.progress > 0).length;
  const overdueActivities = program.activities.filter(a => {
    if (a.completed) return false;
    const deadline = new Date(a.deadline.split('/').reverse().join('-'));
    return deadline < new Date();
  }).length;

  return (
    <Accordion 
      expanded={expanded}
      onChange={onChange}
      sx={{ 
        mb: 1.5,
        boxShadow: 'none', 
        '&:before': { display: 'none' },
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        overflow: 'hidden',
        '&.Mui-expanded': {
          margin: '0 0 12px 0',
        }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ 
          minHeight: 56,
          bgcolor: program.status === 'Activo' ? '#f8f9fa' : '#fafafa',
          '& .MuiAccordionSummary-content': { 
            margin: '8px 0',
            alignItems: 'center'
          },
          '&:hover': {
            bgcolor: program.status === 'Activo' ? '#f0f4f7' : '#f5f5f5',
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 1 }}>
          {/* Program Info */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 600, 
                fontSize: '0.9rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                mr: 1
              }}>
                {program.name}
              </Typography>
              <Chip 
                label={program.status}
                size="small"
                sx={{ 
                  height: 18,
                  fontSize: '0.65rem',
                  bgcolor: program.status === 'Activo' ? '#4caf50' : '#2196f3',
                  color: 'white'
                }}
              />
            </Box>
            <Typography variant="caption" sx={{ 
              color: 'text.secondary',
              fontSize: '0.75rem'
            }}>
              {program.institution} • {program.duration}
            </Typography>
          </Box>

          {/* Quick Stats */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Progress Circle */}
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress 
                variant="determinate" 
                value={program.overallProgress}
                size={36}
                thickness={4}
                sx={{ 
                  color: program.overallProgress === 100 ? '#4caf50' : 
                         program.overallProgress >= 70 ? '#ff9800' : '#2196f3'
                }}
              />
              <Box sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem' }}>
                  {program.overallProgress}%
                </Typography>
              </Box>
            </Box>

            {/* Activity Badges */}
            <Stack direction="column" spacing={0.5}>
              <Tooltip title="Completed Activities">
                <Chip
                  icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}
                  label={`${program.completedActivities}/${program.totalActivities}`}
                  size="small"
                  sx={{ 
                    height: 16,
                    fontSize: '0.65rem',
                    '& .MuiChip-icon': { ml: 0.5 }
                  }}
                />
              </Tooltip>
              {(pendingActivities > 0 || overdueActivities > 0) && (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {pendingActivities > 0 && (
                    <Tooltip title="In Progress">
                      <Chip
                        icon={<PendingIcon sx={{ fontSize: 12 }} />}
                        label={pendingActivities}
                        size="small"
                        sx={{ 
                          height: 16,
                          fontSize: '0.65rem',
                          bgcolor: '#fff3e0',
                          color: '#ff9800',
                          '& .MuiChip-icon': { ml: 0.5, color: '#ff9800' }
                        }}
                      />
                    </Tooltip>
                  )}
                  {overdueActivities > 0 && (
                    <Tooltip title="Overdue">
                      <Chip
                        icon={<WarningIcon sx={{ fontSize: 12 }} />}
                        label={overdueActivities}
                        size="small"
                        sx={{ 
                          height: 16,
                          fontSize: '0.65rem',
                          bgcolor: '#ffebee',
                          color: '#f44336',
                          '& .MuiChip-icon': { ml: 0.5, color: '#f44336' }
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
              )}
            </Stack>
          </Box>
        </Box>
      </AccordionSummary>
      
      <AccordionDetails sx={{ p: 0 }}>
        {/* Detailed Stats Bar */}
        <Box sx={{ 
          px: 2, 
          py: 1.5, 
          bgcolor: '#fafafa',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          gap: 3
        }}>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
              Start Date
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
              {formatInArgentina(new Date(program.start_date), 'dd MMM yyyy')}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
              End Date
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
              {formatInArgentina(new Date(program.end_date), 'dd MMM yyyy')}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                Overall Progress
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                {program.overallProgress}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={program.overallProgress} 
              sx={{ 
                height: 6, 
                borderRadius: 3,
                bgcolor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  bgcolor: program.overallProgress === 100 ? '#4caf50' : 
                          program.overallProgress >= 70 ? '#ff9800' : '#2196f3'
                }
              }}
            />
          </Box>
        </Box>
        
        {/* Activities List */}
        <Box sx={{ p: 1.5, maxHeight: 400, overflowY: 'auto' }}>
          <Typography variant="caption" sx={{ 
            fontWeight: 600, 
            color: 'text.secondary',
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            display: 'block',
            mb: 1
          }}>
            Activities ({program.activities.length})
          </Typography>
          
          {/* Group activities by status */}
          {program.activities
            .sort((a, b) => {
              // Sort: Overdue first, then urgent, then in progress, then completed
              if (!a.completed && !b.completed) {
                const aDeadline = new Date(a.deadline.split('/').reverse().join('-'));
                const bDeadline = new Date(b.deadline.split('/').reverse().join('-'));
                return aDeadline - bDeadline;
              }
              if (a.completed && !b.completed) return 1;
              if (!a.completed && b.completed) return -1;
              return 0;
            })
            .map((activity, index) => (
              <ActivityItem key={index} activity={activity} />
            ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const ParticipantSider = ({ phoneNumber, onClose }) => {
  const [participant, setParticipant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [expandedProgram, setExpandedProgram] = useState(false);

  // Mock data for demonstration
  const mockParticipant = {
    first_name: 'María',
    last_name: 'González',
    email: 'maria.gonzalez@email.com',
    whatsapp_number: phoneNumber,
    country: 'Argentina',
    age: 28,
    gender: 'Femenino',
    education_level: 'Universitario Completo',
    employment_status: 'Empleado',
    scholarship_provider: 'IBM SkillsBuild',
    alumni_date: '2024-08', // Alumni since August 2024
    application_date: '2025-08-15',
    updated_at: '2025-08-30',
    last_login: '2025-08-29T14:30:00',
    total_study_hours: 124.5,
    academic_programs: [
      {
        id: 1,
        name: 'Fundamentos de Inteligencia Artificial',
        institution: 'IBM SkillsBuild + Guayerd',
        duration: '6 meses',
        status: 'Activo',
        start_date: '2025-08-01',
        end_date: '2026-02-01',
        overallProgress: 78,
        completedActivities: 7,
        totalActivities: 12,
        activities: [
          {
            name: 'Módulo 1: Introducción a la IA',
            type: 'Módulo Asincrónico',
            description: 'Conceptos básicos de inteligencia artificial y machine learning',
            progress: 100,
            completed: true,
            deadline: '15/09/2025',
            grade: 'A+'
          },
          {
            name: 'Módulo 2: Matemáticas para IA',
            type: 'Módulo Asincrónico',
            description: 'Álgebra lineal, cálculo y estadística aplicada a IA',
            progress: 85,
            completed: false,
            deadline: '30/08/2025',
            grade: null
          },
          {
            name: 'Clase Sincrónica 1',
            type: 'Presentismo',
            description: 'Sesión de preguntas y respuestas sobre Módulo 1',
            progress: 100,
            completed: true,
            deadline: '20/09/2025',
            grade: 'Presente'
          },
          {
            name: 'Trabajo Práctico 1',
            type: 'Evaluación',
            description: 'Implementación de algoritmo de clasificación simple',
            progress: 100,
            completed: true,
            deadline: '25/09/2025',
            grade: '9.5/10'
          },
          {
            name: 'Módulo 3: Redes Neuronales',
            type: 'Módulo Asincrónico',
            description: 'Fundamentos de deep learning y redes neuronales',
            progress: 45,
            completed: false,
            deadline: '02/09/2025',
            grade: null
          }
        ]
      },
      {
        id: 2,
        name: 'Desarrollo Web Full Stack',
        institution: 'Guayerd',
        duration: '4 meses',
        status: 'Completado',
        start_date: '2024-09-01',
        end_date: '2025-01-01',
        overallProgress: 100,
        completedActivities: 15,
        totalActivities: 15,
        activities: [
          {
            name: 'Módulo Frontend',
            type: 'Módulo Asincrónico',
            description: 'React, HTML, CSS y JavaScript avanzado',
            progress: 100,
            completed: true,
            deadline: '15/10/2024',
            grade: 'A'
          },
          {
            name: 'Módulo Backend',
            type: 'Módulo Asincrónico',
            description: 'Node.js, Express y bases de datos',
            progress: 100,
            completed: true,
            deadline: '15/11/2024',
            grade: 'A-'
          },
          {
            name: 'Proyecto Final',
            type: 'Evaluación',
            description: 'Aplicación web completa con frontend y backend',
            progress: 100,
            completed: true,
            deadline: '01/01/2025',
            grade: '9/10'
          }
        ]
      }
    ]
  };

  useEffect(() => {
    const fetchParticipantDetails = async () => {
      if (!phoneNumber) {
        setLoading(false);
        setError('No phone number provided.');
        return;
      }
      
      setLoading(true);
      setError(null);
      try {
        const data = await conversationService.getParticipantDetails(phoneNumber);
        setParticipant(data);
      } catch (err) {
        console.error('Error fetching participant details:', err);
        // Instead of showing error, show mock data for now
        setParticipant(mockParticipant);
        setError(null);
      } finally {
        setLoading(false);
      }
    };

    fetchParticipantDetails();
  }, [phoneNumber]);

  const phoneInfo = participant ? getPhoneCountryInfo(participant.whatsapp_number) : getPhoneCountryInfo(phoneNumber);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleProgramExpand = (programId) => (event, isExpanded) => {
    setExpandedProgram(isExpanded ? programId : false);
  };

  // Calculate overall statistics
  const calculateStats = () => {
    if (!participant?.academic_programs) return null;
    
    const totalPrograms = participant.academic_programs.length;
    const activePrograms = participant.academic_programs.filter(p => p.status === 'Activo').length;
    const completedPrograms = participant.academic_programs.filter(p => p.status === 'Completado').length;
    
    let totalActivities = 0;
    let completedActivities = 0;
    let pendingActivities = 0;
    let overdueActivities = 0;
    
    participant.academic_programs.forEach(program => {
      totalActivities += program.totalActivities;
      completedActivities += program.completedActivities;
      
      program.activities.forEach(activity => {
        if (!activity.completed) {
          if (activity.progress > 0) pendingActivities++;
          
          const deadline = new Date(activity.deadline.split('/').reverse().join('-'));
          if (deadline < new Date()) overdueActivities++;
        }
      });
    });
    
    return {
      totalPrograms,
      activePrograms,
      completedPrograms,
      totalActivities,
      completedActivities,
      pendingActivities,
      overdueActivities,
      completionRate: Math.round((completedActivities / totalActivities) * 100)
    };
  };

  const stats = calculateStats();

  return (
    <Box
      sx={{
        width: 450,
        minWidth: 450,
        height: '100%',
        borderLeft: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'white',
      }}
    >
      <Box sx={{ 
        height: 56, 
        boxSizing: 'border-box', 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        borderBottom: '1px solid #e0e0e0',
        py: 1.5,
        px: 2,
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
          Participant Profile
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        )}
        {error && !loading && (
          <Alert severity="warning">{error}</Alert>
        )}
        {!loading && !error && participant && (
          <>
            {/* Compact Header with Avatar and Key Info */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              p: 2, 
              borderBottom: '1px solid #e0e0e0',
              bgcolor: '#f8f9fa'
            }}>
              <Avatar sx={{ 
                width: 56, 
                height: 56, 
                bgcolor: '#0078D4', 
                fontSize: '1.25rem',
                mr: 2,
                flexShrink: 0,
              }}>
                {participant.first_name?.[0]}{participant.last_name?.[0]}
              </Avatar>
              <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Tooltip title="Alumni Guayerd" arrow placement="top">
                  <Chip 
                    label={participant.alumni_date ? formatInArgentina(new Date(participant.alumni_date + '-01'), 'MMM yyyy') : 'Aug 2024'} 
                    size="small" 
                    sx={{ 
                      height: 18, 
                      fontSize: '0.65rem', 
                      bgcolor: '#e3f2fd', 
                      color: '#1976d2', 
                      mb: 0.1,
                      alignSelf: 'flex-start'
                    }} 
                  />
                </Tooltip>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.95rem', mb: 0.1, lineHeight: 1.2 }}>
                  {participant.first_name} {participant.last_name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', fontSize: '0.75rem' }}>
                  <AccessTimeIcon sx={{ fontSize: 13, mr: 0.5 }} />
                  Last activity: {formatInArgentina(new Date(participant.last_login || new Date()), 'dd/MM HH:mm')}
                </Typography>
              </Box>
            </Box>

            {/* Quick Stats Overview */}
            {stats && (
              <Box sx={{ 
                px: 2, 
                py: 1.5, 
                borderBottom: '1px solid #e0e0e0',
                bgcolor: 'white'
              }}>
                <Grid container spacing={1}>
                  <Grid item xs={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {stats.activePrograms}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                        Active Programs
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                        {stats.completionRate}%
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                        Completed
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#ff9800' }}>
                        {stats.pendingActivities}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                        In Progress
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: stats.overdueActivities > 0 ? '#f44336' : 'text.secondary' }}>
                        {stats.overdueActivities}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                        Overdue
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{
                  minHeight: 40,
                  '& .MuiTab-root': {
                    minHeight: 40,
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                  }
                }}
              >
                <Tab label="Information" />
                <Tab label="Programs" />
                <Tab label="Performance" />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <Box sx={{ p: 2 }}>
              {/* Tab 1: Personal Information */}
              {activeTab === 0 && (
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <CompactDetailItem icon={EmailIcon} label="Email" value={participant.email} />
                    </Grid>
                    <Grid item xs={6}>
                      <CompactDetailItem icon={PhoneIcon} label="WhatsApp" value={phoneInfo.formattedNumber || participant.whatsapp_number} />
                    </Grid>
                    <Grid item xs={6}>
                      <CompactDetailItem icon={FlagIcon} label="Country" value={participant.country} />
                    </Grid>
                    <Grid item xs={6}>
                      <CompactDetailItem icon={CakeIcon} label="Age" value={participant.age ? `${participant.age} years` : '-'} />
                    </Grid>
                    <Grid item xs={6}>
                      <CompactDetailItem icon={SchoolIcon} label="Education" value={participant.education_level} />
                    </Grid>
                    <Grid item xs={6}>
                      <CompactDetailItem icon={WorkIcon} label="Employment" value={participant.employment_status} />
                    </Grid>
                    <Grid item xs={6}>
                      <CompactDetailItem icon={EventIcon} label="Enrollment" value={participant.application_date ? formatInArgentina(new Date(participant.application_date), 'dd/MM/yyyy') : '-'} />
                    </Grid>
                    <Grid item xs={6}>
                      <CompactDetailItem icon={AccessTimeIcon} label="Study Hours" value={`${participant.total_study_hours}h`} />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Tab 2: Academic Programs */}
              {activeTab === 1 && (
                <Box>
                  {participant.academic_programs?.map((program) => (
                    <ProgramAccordion 
                      key={program.id} 
                      program={program}
                      expanded={expandedProgram === program.id}
                      onChange={handleProgramExpand(program.id)}
                    />
                  ))}
                  
                  {(!participant.academic_programs || participant.academic_programs.length === 0) && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <SchoolIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        No academic programs registered
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Tab 3: Performance Metrics */}
              {activeTab === 2 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                    Performance Metrics
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                          Completion Rate by Program
                        </Typography>
                        {participant.academic_programs?.map(program => (
                          <Box key={program.id} sx={{ mb: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                {program.name}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                {program.overallProgress}%
                              </Typography>
                            </Box>
                            <LinearProgress 
                              variant="determinate" 
                              value={program.overallProgress}
                              sx={{ 
                                height: 6, 
                                borderRadius: 3,
                                bgcolor: '#e0e0e0',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: program.overallProgress === 100 ? '#4caf50' : 
                                          program.overallProgress >= 70 ? '#ff9800' : '#2196f3'
                                }
                              }}
                            />
                          </Box>
                        ))}
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ParticipantSider;